import { Meteo15DaysDescriptionProps } from '../Meteo15DaysDescription/Meteo15DaysDescription.component';
import { MeteoOthersTools } from '../MeteoOthersTools/MeteoOthersTools.component';
import { MeteoPrimaryTools } from '../MeteoPrimaryTools/MeteoPrimaryTools.component';
import { MeteoUserActionWrapperProps } from '../MeteoUserActionWrapper/MeteoUserActionWrapper';
import { useMetag } from './MetaTag.hook';
import { MeteoHomeContext } from 'pages/meteo/[type]/[locality]';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { ADVERT_FORMAT_ID, ADVERT_PAGE_ID } from '@adverts/business/Adverts.contants';
import { useAdvert } from '@adverts/business/useAdvert';
import { AdProps } from '@adverts/components/GenericAd.component';
import { BreadCrumbProps } from '@components/BreadCrumb/BreadCrumb.component';
import { BreadCrumbStep } from '@components/BreadCrumb/BreadCrumb.types';
import { FreeFieldComponentProps } from '@components/FreeFieldComponent/FreeFieldComponent.component';
import { PlcTextListLoader } from '@components/Loaders/PlcTextLoader.component';
import { Text } from '@components/Text/Text.component';
import { PlcPageLayout } from '@layout/components/PlcPageLayout/PlcPageLayout.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { MeteoHomeTabs } from '@meteo/business/Meteo.types';
import { MeteoHomeSummary } from '@meteo/components/MeteoHomeSummary/MeteoHomeSummary.component';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { Spacing } from '@styles/Spacing.style';
import { userSelectors } from '@user/business/User.store';
import { isBrowser } from '@utils/server';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import dynamic from 'next/dynamic';
import { MeteoBoxLinksProps } from '../MeteoBoxLinks/types';

import './MeteoHomeLayout.scss';

const MeteoBoxLinks = dynamic<MeteoBoxLinksProps>(
  () => import('@meteo/components/MeteoBoxLinks/MeteoBoxLinks.component'),
  {
    loading: () => (
      <PlcTextListLoader
        textProps={[{ bottomSpacing: Spacing.xl, numberOfLine: 7, variant: 'p', width: 600 }]}
        uniqueKey="MeteoBoxLinks"
      />
    ),
    ssr: false,
  }
);

const GenericAd = dynamic<AdProps>(
  () => import('@adverts/components/GenericAd.component').then(module => module.GenericAd),
  {
    ssr: false,
  }
);

const BreadCrumb = dynamic<BreadCrumbProps>(
  () => import('@components/BreadCrumb/BreadCrumb.component').then(module => module.BreadCrumb),
  {
    loading: () => <div className="plc-p-basis tiny none block-up-m">&nbsp;</div>,
    ssr: false,
  }
);

const MeteoUserActionWrapper = dynamic<MeteoUserActionWrapperProps>(
  () => import('../MeteoUserActionWrapper/MeteoUserActionWrapper').then(module => module.MeteoUserActionWrapper),
  {
    ssr: false,
  }
);

const MeteoHomeForecastTabs = dynamic<{}>(
  () => import('../MeteoHomeForecastTabs/MeteoHomeForecastTabs.component').then(module => module.MeteoHomeForecastTabs),
  {
    loading: () => <MeteoHomeTabAndCityMock />,
  }
);

const MeteoHomeTabAndCityMock = dynamic<{}>(
  () =>
    import('../MeteoHomeTabAndCityMock/MeteoHomeTabAndCityMock.component').then(
      module => module.MeteoHomeTabAndCityMock
    ),
  {
    ssr: false,
  }
);

const FreeFieldComponent = dynamic<FreeFieldComponentProps>(
  () => import('@components/FreeFieldComponent/FreeFieldComponent.component').then(module => module.FreeFieldComponent),
  {
    ssr: true,
  }
);

const Meteo15DaysDescription = dynamic<Meteo15DaysDescriptionProps>(() =>
  import('../Meteo15DaysDescription/Meteo15DaysDescription.component').then(module => module.Meteo15DaysDescription)
);

const BREADCRUMB_STEPS: BreadCrumbStep[] = [
  {
    label: 'common.newspaper-short',
    linkProps: { as: urlEnum.journal, href: urlEnum.journal },
  },
  {
    label: 'common.the-weather',
  },
];

function MeteoHomeLayout() {
  const isSmartphone = useIsBreakpointDown(Breakpoint.s);
  const isMobile = useIsBreakpointDown(Breakpoint.m);
  const { t } = useTranslation(['meteo']);
  const isSSO = useSelector(userSelectors.isSSO);
  const isPro = useSelector(userSelectors.isPro);

  const {
    defaultTab: { data: activeTab },
    freeFieldContent: { data: freeFieldContent, isValidating: isFreeFieldLoading },
    reversedCity: city,
    servicesContent,
  } = useContext(MeteoHomeContext);

  const isTrends = activeTab === MeteoHomeTabs.TwentyHeightDays;
  const duration = t(`meteo.duration.${activeTab}`);

  const { meta, metaOptions, tagProps } = useMetag(city, activeTab);

  const title = metaOptions
    ? {
        i18nKey: isTrends ? 'meteo.home.city.trends-title' : 'meteo.home.city.forecast-title',
        i18nOptions: {
          ...metaOptions,
          duration,
        },
      }
    : { i18nKey: isTrends ? 'meteo.home.trends-title' : 'meteo.home.forecast-title', i18nOptions: { duration } };

  useAdvert({
    pageId: ADVERT_PAGE_ID.MeteoHome,
  });

  const display15DaysDescMobile = activeTab === MeteoHomeTabs.FifteenDays && isMobile && !isPro;

  return (
    <PlcPageLayout className={classnames('meteo', isSSO && 'plc-pt-basis')} {...meta} tagProps={tagProps}>
      <GenericAd formatId={isSmartphone ? ADVERT_FORMAT_ID.MegaBanner : ADVERT_FORMAT_ID.Arch} />
      <BreadCrumb className="wrapper-s plc-mb-basis" customSpacing steps={BREADCRUMB_STEPS} />
      <MeteoHomeSummary />
      <div className="forecasts-title wrapper-s flex space-between plc-my-m plc-mb-up-m-l plc-mt-up-m-xxl">
        <Text {...title} className="plc-mb-s" tag="h1" variant="h3" />
        {isMobile && <MeteoUserActionWrapper />}
      </div>
      {display15DaysDescMobile && (
        <Meteo15DaysDescription isMobile={activeTab === MeteoHomeTabs.FifteenDays && isMobile} />
      )}
      {!isMobile && <MeteoUserActionWrapper className="wrapper-s" />}
      <div>
        {city && <MeteoHomeForecastTabs />}
        {!city && isBrowser() && <MeteoHomeTabAndCityMock />}
      </div>
      {!isSSO && (
        <>
          <GenericAd className="wrapper-s InlineVideoIndex" formatId={ADVERT_FORMAT_ID.InlineVideo} />
          <MeteoPrimaryTools serviceContent={servicesContent} />
          <MeteoOthersTools />
          {freeFieldContent && <FreeFieldComponent content={freeFieldContent} isLoading={isFreeFieldLoading} />}
          <MeteoBoxLinks activeTab={activeTab || MeteoHomeTabs.ThirtySixHours} />
        </>
      )}
    </PlcPageLayout>
  );
}

export { MeteoHomeLayout };
